import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2Icon,
  Loader2Icon,
  PauseIcon,
  PlayIcon,
  ShieldAlertIcon,
  SlidersHorizontalIcon,
} from 'lucide-react';
import {
  account,
  app,
  bridge,
  migration as migrationApi,
  track,
  type MigrationFilter,
  type MigrationSnapshot,
  type MigrationSourceStats,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { SelectRow } from '@/components/SelectRow';
import { StepCard } from '@/components/StepCard';
import { taskStatusLabel } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD } from '@/components/ui';
import { cn } from '@/lib/utils';

/** 可迁移的数据类型（与原生 MigrationContentType 对齐）。 */
const TYPES = [
  { key: 'posts', label: '作品' },
  { key: 'likes', label: '点赞' },
  { key: 'collects', label: '收藏' },
  { key: 'collectFolders', label: '收藏夹' },
  { key: 'following', label: '关注' },
];

/** 迁移页自己的状态措辞（照搬搬小书的 overrides 写法）。 */
const STATUS_LABELS: Record<string, string> = {
  preparing: '迁移前补拉中',
  running: '迁移中',
};

/** 时间范围选项（对齐搬小书 NoteMigrationTimeFilter）。 */
const TIME_RANGES = [
  { key: 'all', label: '全部' },
  { key: 'sixMonths', label: '最近半年' },
  { key: 'oneYear', label: '最近 1 年' },
  { key: 'twoYears', label: '最近 2 年' },
] as const;

/** 条数上限预设（null = 全部）。 */
const COUNT_PRESETS: Array<number | null> = [null, 10, 50, 100];

/** 有发布时间信号、可选时间范围的类型。 */
const TIME_FILTERABLE = new Set(['posts', 'likes', 'collects']);

function timeRangeLabel(key: MigrationFilter['timeRange']): string {
  return TIME_RANGES.find((t) => t.key === key)?.label ?? '全部';
}

/** 把筛选归一化：全为默认则视为无筛选。 */
function normalizeFilter(filter: MigrationFilter): MigrationFilter | null {
  const next: MigrationFilter = {};
  if (filter.maxCount && filter.maxCount > 0) next.maxCount = filter.maxCount;
  if (filter.timeRange && filter.timeRange !== 'all') next.timeRange = filter.timeRange;
  return Object.keys(next).length > 0 ? next : null;
}

function filterSummary(filter?: MigrationFilter): string | null {
  const parts: string[] = [];
  if (filter?.maxCount) parts.push(`最多 ${filter.maxCount} 条`);
  if (filter?.timeRange && filter.timeRange !== 'all') {
    parts.push(timeRangeLabel(filter.timeRange));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function typeLabelOf(key: string): string {
  return TYPES.find((t) => t.key === key)?.label ?? key;
}

export function MigrationPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const {
    accounts,
    selectedId,
    setSelectedId,
    error: accountError,
    loading: accountsLoading,
    reload: reloadAccounts,
  } = useAccounts();
  const [targetId, setTargetId] = useState('');
  // 默认什么都不勾选（用户勾了才进入「选目标账号」）
  const [types, setTypes] = useState<string[]>([]);
  // 逐类型筛选（条数上限 / 时间范围），key 为类型名
  const [filters, setFilters] = useState<Record<string, MigrationFilter>>({});
  // 「点赞转收藏」：迁移点赞时按收藏写入目标账号
  const [likesAsCollects, setLikesAsCollects] = useState(false);
  const [filterSheetType, setFilterSheetType] = useState<string | null>(null);
  const [stats, setStats] = useState<MigrationSourceStats | null>(null);
  const [snapshot, setSnapshot] = useState<MigrationSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const snapshotRevision = useRef(0);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const loaded = !infoLoading && !accountsLoading;
  useAppReady(loaded);

  // 埋点：页面访问
  useEffect(() => {
    track.pageOpen('migration');
  }, []);

  // 原生登录流程结束后会发 account.changed，自动刷新账号列表
  useEffect(() => {
    const off = bridge.on('account.changed', () => reloadAccounts());
    return off;
  }, [reloadAccounts]);

  const source = accounts.find((a) => a.id === selectedId) ?? null;
  const target = accounts.find((a) => a.id === targetId) ?? null;
  // 可写账号；演示账号也能当演示流程的目标（写入走本地模拟）。
  const eligibleTargets = accounts.filter(
    (a) => (a.canWrite || a.isDemo) && a.id !== source?.id,
  );

  // 切换账号时重新订阅并读取快照；晚到的查询结果不能覆盖更新的事件或操作。
  useEffect(() => {
    setSnapshot(null);
    setError(null);
    if (!selectedId) return;
    let cancelled = false;
    const revision = ++snapshotRevision.current;
    const off = bridge.on('migration.progress', (payload) => {
      const next = payload as MigrationSnapshot;
      if (next.accountId !== selectedId) return;
      snapshotRevision.current += 1;
      setSnapshot(next);
    });
    migrationApi
      .state(selectedId)
      .then((next) => {
        if (!cancelled && snapshotRevision.current === revision) setSnapshot(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      off();
    };
  }, [selectedId]);

  // 源账号可迁移数据条数：本地已备份优先，缺失时读实时 stats 兜底
  useEffect(() => {
    if (!source) {
      setStats(null);
      return;
    }
    let cancelled = false;
    migrationApi
      .sourceStats(source.id)
      .then((next) => {
        if (!cancelled) setStats(next);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [source?.id]);

  const state = snapshot?.accountId === selectedId ? snapshot : null;

  // 埋点：迁移完成 / 失败 / 暂停（只在状态跳转时各发一次）
  const prevMigrationStateRef = useRef<string | null>(null);
  useEffect(() => {
    const current = state?.state ?? null;
    const prev = prevMigrationStateRef.current;
    prevMigrationStateRef.current = current;
    if (prev == null || current === prev) return;
    if (current === 'completed') track.event('migration_complete');
    else if (current === 'failed') track.event('migration_fail');
    else if (current === 'paused') track.event('migration_pause');
  }, [state?.state]);

  const preparing = state?.state === 'preparing';
  const active = preparing || state?.state === 'pending' || state?.state === 'running' || state?.state === 'paused';
  const completed = state?.state === 'completed';
  const failed = state?.state === 'failed';
  const blocked = state?.state === 'blocked';
  const counts = state?.counts;
  const ratio =
    counts && counts.total > 0 ? Math.min(1, counts.migrated / counts.total) : undefined;

  const step1Done = !!source;
  const step2Done = types.length > 0;
  const step3Done = !!target && target.id !== source?.id;

  const pickSource = useCallback(async () => {
    try {
      const picked = await account.pick('all');
      if (picked.id === targetId) {
        setError('源账号和目标账号不能相同，请另选一个源账号');
        return;
      }
      setSelectedId(picked.id);
      setError(null);
    } catch {
      // 用户取消
    }
  }, [setSelectedId, targetId]);

  const pickTarget = useCallback(async () => {
    try {
      const picked = await account.pick('authenticated');
      if (picked.id === selectedId) {
        setError('目标账号不能和源账号相同');
        return;
      }
      setTargetId(picked.id);
      setError(null);
    } catch {
      // 用户取消
    }
  }, [selectedId]);

  const toggleType = useCallback(
    (key: string) => {
      if (busy || active) return;
      setTypes((prev) => {
        if (prev.includes(key)) {
          // 取消勾选时清掉该类型的筛选，避免残留
          setFilters((f) => {
            if (!f[key]) return f;
            const next = { ...f };
            delete next[key];
            return next;
          });
          return prev.filter((t) => t !== key);
        }
        return [...prev, key];
      });
    },
    [active, busy],
  );

  const updateFilter = useCallback((key: string, patch: Partial<MigrationFilter>) => {
    setFilters((prev) => {
      const merged = normalizeFilter({ ...prev[key], ...patch });
      const next = { ...prev };
      if (merged) next[key] = merged;
      else delete next[key];
      return next;
    });
  }, []);

  const run = useCallback(
    async (action: 'start' | 'pause' | 'resume', force = false) => {
      if (!source || busy) return;
      if (action === 'start' && (!target || target.id === source.id)) return;
      setBusy(true);
      setError(null);
      const revision = ++snapshotRevision.current;
      if (action === 'start') {
        setSnapshot({ accountId: source.id, state: 'preparing' });
      }
      if (action === 'start') {
        track.event('migration_start', { types, force, likesAsCollects });
      }
      try {
        const activeFilters = Object.fromEntries(
          Object.entries(filters).filter(([key]) => types.includes(key)),
        );
        const next =
          action === 'start'
            ? await migrationApi.start(
                source.id,
                target!.id,
                types,
                false,
                activeFilters,
                force,
                types.includes('likes') && likesAsCollects,
              )
            : action === 'pause'
              ? await migrationApi.pause(source.id)
              : await migrationApi.resume(source.id);
        if (selectedIdRef.current === source.id && snapshotRevision.current === revision) {
          setSnapshot(next);
        }
      } catch (e) {
        if (selectedIdRef.current === source.id) {
          if (action === 'start' && snapshotRevision.current === revision) setSnapshot(null);
          setError(describeError(e));
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, source, target, types, filters, likesAsCollects],
  );

  const reset = useCallback(() => {
    snapshotRevision.current += 1;
    setSnapshot(null);
    setError(null);
    setTypes([]);
    setFilters({});
    setLikesAsCollects(false);
    setTargetId('');
  }, []);

  // 源账号是否有可迁移的数据（本地或实时统计）
  const hasAnyData = useMemo(() => {
    if (!stats) return false;
    return stats.hasLocalBackup || Object.values(stats.counts).some((n) => n > 0);
  }, [stats]);

  const countOf = (key: string) => stats?.counts?.[key] ?? 0;

  return (
    <PageShell title="迁移控制台" description="搬小音 · 由原生 BridgeWebView 承载" backTo="/">
      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {loaded && accounts.length === 0 ? (
        <Card className={CARD}>
          <div className="flex items-center gap-2">
            <ShieldAlertIcon className="text-primary size-5 shrink-0" />
            <strong className="text-sm font-semibold">没有可用的账号</strong>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            迁移需要一个源账号和一个已登录的目标账号，请先在 App 内登录后再来。
          </p>
        </Card>
      ) : null}

      {/* 门禁拦截：目标账号试收藏探针没写进去，疑似风控 */}
      {blocked ? (
        <Card className={CARD}>
          <div className="flex items-center gap-2">
            <ShieldAlertIcon className="text-destructive size-5 shrink-0" />
            <strong className="text-sm font-semibold">目标账号暂时无法写入</strong>
          </div>
          <p className="text-muted-foreground mt-1 text-xs leading-snug">
            {state?.note ??
              '用目标账号试收藏了一条笔记，但没有成功写入，账号可能处于风控中。'}
          </p>
          <p className="text-muted-foreground mt-1 text-caption1 leading-snug">
            建议稍等一段时间再试（新号或刚做过大量操作时容易被限制）。
          </p>
          <div className="mt-2.5 flex flex-col gap-2">
            <Button
              className="w-full"
              loading={busy}
              onClick={() => void run('start', true)}
            >
              <PlayIcon data-icon="inline-start" />
              仍要尝试迁移
            </Button>
            <Button variant="secondary" className="w-full" onClick={reset}>
              重新选择
            </Button>
          </div>
        </Card>
      ) : null}

      {/* 完成态：独立结果页（大图标 + overview + 后续动作，不再显示步骤流程） */}
      {completed ? (
        <Card className={CARD}>
          <div className="flex flex-col items-center py-5 text-center">
            <CheckCircle2Icon className="text-primary size-14" />
            <strong className="mt-3 text-title3 font-semibold">迁移完成</strong>
            <p className="text-muted-foreground mt-1 text-sm tabular-nums">
              已迁移 {counts?.migrated ?? 0} / {counts?.total ?? 0} 条
              {counts && counts.failed > 0 ? ` · 失败 ${counts.failed} 条` : ''}
            </p>
            {state?.selectedTypes?.length ? (
              <p className="text-muted-foreground mt-1 text-caption1">
                迁移内容：{state.selectedTypes.map(typeLabelOf).join('、')}
                {state.likesAsCollects && state.selectedTypes.includes('likes')
                  ? '（点赞转收藏）'
                  : ''}
              </p>
            ) : null}
            <div className="mt-4 flex w-full flex-col gap-2">
              {target ? (
                <Button
                  className="w-full"
                  onClick={() =>
                    void app.openNative('account', { accountId: target.id })
                  }
                >
                  去目标账号检查数据
                </Button>
              ) : null}
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => void app.openNative('home')}
              >
                回到主页
              </Button>
            </div>
            <button
              type="button"
              className="text-muted-foreground mt-3 text-caption1 underline"
              onClick={reset}
            >
              再迁移一次
            </button>
          </div>
        </Card>
      ) : null}

      {/* 结束态（失败 / 其它）卡片 */}
      {state &&
      state.state !== 'idle' &&
      state.state !== 'blocked' &&
      state.state !== 'completed' &&
      !active ? (
        <Card className={CARD}>
          <div className="flex items-center gap-2">
            {completed ? (
              <CheckCircle2Icon className="size-5 shrink-0" />
            ) : (
              <ShieldAlertIcon className="text-destructive size-5 shrink-0" />
            )}
            <strong className="text-sm font-semibold">
              {completed ? '迁移完成' : (taskStatusLabel(state.state) ?? '迁移结束')}
            </strong>
          </div>
          <p className="text-muted-foreground mt-1 text-xs tabular-nums">
            {completed
              ? counts
                ? `已迁移 ${counts.migrated} / ${counts.total} 条${counts.failed > 0 ? ` · 失败 ${counts.failed}` : ''}`
                : '已处理完成'
              : (state.error
                  ? describeError(state.error)
                  : '可重新开始迁移')}
          </p>
          {failed && state.taskId ? (
            <p className="text-muted-foreground mt-1 text-caption1 leading-snug">
              目标账号若已退出登录，请重新登录目标账号后点「继续迁移」接着跑。
            </p>
          ) : null}
          <div className="mt-2.5 flex flex-col gap-2">
            {failed && state.taskId ? (
              <Button
                className="w-full"
                loading={busy}
                onClick={() => void run('resume')}
              >
                <PlayIcon data-icon="inline-start" />
                重新登录后继续迁移
              </Button>
            ) : null}
            <Button variant="secondary" className="w-full" onClick={reset}>
              再迁移一次
            </Button>
          </div>
        </Card>
      ) : null}

      {!active && !completed ? (
        <>
          <StepCard
            number={1}
            title="源账号"
            subtitle={step1Done ? '点击可重新选择' : '从这里的内容迁出（不必保持登录）'}
            state={step1Done ? 'complete' : 'active'}
          >
            <AccountCard
              account={source}
              emptyText="选择源账号"
              onClick={() => void pickSource()}
              flat
            />
          </StepCard>

          {step1Done ? (
            <StepCard
              number={2}
              title="选择要迁移的数据"
              subtitle={
                step2Done
                  ? `已选 ${types.length} 类`
                  : hasAnyData
                    ? '至少选择一种'
                    : '该账号没有可迁移的数据'
              }
              state={step2Done ? 'complete' : 'active'}
            >
              <div className="flex flex-col gap-2">
                {TYPES.map((t) => {
                  const summary = filterSummary(filters[t.key]);
                  return (
                    <SelectRow
                      key={t.key}
                      checked={types.includes(t.key)}
                      onCheckedChange={() => toggleType(t.key)}
                      detail={`${countOf(t.key)} 条`}
                      action={
                        types.includes(t.key) ? (
                          <button
                            type="button"
                            onClick={() => setFilterSheetType(t.key)}
                            disabled={busy || active}
                            className={cn(
                              'rounded-control flex items-center gap-1 px-2 py-1 text-caption1',
                              summary ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
                            )}
                          >
                            <SlidersHorizontalIcon className="size-3.5" />
                            {summary ?? '筛选'}
                          </button>
                        ) : null
                      }
                    >
                      {t.label}
                    </SelectRow>
                  );
                })}
                {types.includes('likes') ? (
                  <SelectRow
                    checked={likesAsCollects}
                    onCheckedChange={() => {
                      if (busy || active) return;
                      setLikesAsCollects((prev) => !prev);
                    }}
                    detail="点赞 → 收藏"
                    footer="开启后，点赞过的内容会以收藏形式迁到目标账号（目标账号不点赞）。"
                  >
                    点赞转收藏
                  </SelectRow>
                ) : null}
              </div>
              {stats ? (
                <p className="text-muted-foreground mt-2 text-caption1 leading-snug">
                  {stats.hasLocalBackup
                    ? '数字为本机已备份条数。'
                    : '本机还没有备份，数字来自实时统计；开始迁移时会先自动备份这些数据。'}
                </p>
              ) : null}
            </StepCard>
          ) : null}

          {step1Done && step2Done ? (
            <StepCard
              number={3}
              title="目标账号"
              subtitle={step3Done ? '点击可重新选择' : '迁到这里（需已登录，且不能和源账号相同）'}
              state={step3Done ? 'complete' : 'active'}
            >
              {eligibleTargets.length > 0 ? (
                <AccountCard
                  account={target}
                  emptyText="选择目标账号"
                  onClick={() => void pickTarget()}
                  flat
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-muted-foreground text-xs leading-snug">
                    还没有可用的目标账号，需要先登录另一个账号（登录后本页会自动刷新）。
                  </p>
                  <Button className="w-full" onClick={() => void app.openNative('login')}>
                    去登录目标账号
                  </Button>
                  <Button variant="secondary" className="w-full" onClick={reloadAccounts}>
                    刷新账号列表
                  </Button>
                </div>
              )}
            </StepCard>
          ) : null}

          {step1Done && step2Done && step3Done ? (
            <StepCard
              number={4}
              title="确认并开始"
              subtitle="会先自动备份源数据（增量补拉），再开始迁移"
              state={completed ? 'complete' : 'active'}
            >
              <p className="text-muted-foreground text-caption1 leading-snug">
                未备份过的数据会先自动拉取到本机，再逐条写入目标账号；源账号离线时直接用本机已有数据。
              </p>
              <Button
                className="mt-2.5 w-full"
                loading={busy}
                onClick={() => void run('start')}
              >
                {!busy ? <PlayIcon data-icon="inline-start" /> : null}
                {busy ? '处理中…' : '开始迁移'}
              </Button>
            </StepCard>
          ) : null}
        </>
      ) : null}

      {error ?? accountError ? (
        <p className="text-destructive px-1 text-sm">{error ?? accountError}</p>
      ) : null}

      {!loaded ? <PageSkeleton kind="migration" rows={3} /> : null}

      {/* 开始后的遮罩层：当前进度 / 步骤 / 错误 */}
      {active ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6 backdrop-blur-[2px]">
          <Card className={cn(CARD, 'w-full max-w-sm')}>
            <div className="flex items-center gap-2">
              {state!.state === 'paused' ? (
                <PauseIcon className="text-muted-foreground size-5 shrink-0" />
              ) : (
                <Loader2Icon className="text-primary size-5 shrink-0 animate-spin" />
              )}
              <strong className="text-sm font-semibold">
                {taskStatusLabel(state!.state, STATUS_LABELS) ?? state!.state}
              </strong>
              {counts ? (
                <span className="text-muted-foreground ml-auto text-footnote tabular-nums">
                  {counts.migrated} / {counts.total}
                </span>
              ) : null}
            </div>

            {counts && counts.total > 0 ? (
              <div className="bg-muted mt-3 h-1.5 w-full overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full rounded-full transition-[width] duration-300"
                  style={{ width: `${Math.round((ratio ?? 0) * 100)}%` }}
                />
              </div>
            ) : null}

            <p className="text-muted-foreground mt-3 text-xs leading-snug">
              {preparing
                ? state?.prefetch
                  ? `正在补拉：${typeLabelOf(state.prefetch.type)} · 已处理 ${state.prefetch.processed} 条`
                  : (state?.note ?? '正在把源账号本次要迁的数据增量拉到本机…')
                : state!.state === 'paused'
                  ? '已暂停，点「继续迁移」接着跑'
                  : `正在逐条迁移${state?.selectedTypes?.length ? `（${state.selectedTypes.length} 类）` : ''}`}
            </p>
            {preparing && state?.prefetch?.item ? (
              <p className="text-foreground mt-1 truncate text-xs">
                {state.prefetch.item}
              </p>
            ) : null}
            {state?.current ? (
              <p className="text-foreground mt-1 truncate text-xs">{state.current}</p>
            ) : null}
            {counts && counts.failed > 0 ? (
              <p className="text-destructive mt-1 text-xs tabular-nums">失败 {counts.failed} 条</p>
            ) : null}
            {state?.note && !preparing ? (
              <p className="text-muted-foreground mt-1 text-caption1 leading-snug">{state.note}</p>
            ) : null}
            {state?.error ? (
              <p className="text-destructive mt-1 text-caption1 leading-snug">
                {describeError(state.error)}
              </p>
            ) : null}

            <div className="mt-3">
              {state!.state === 'running' ? (
                <Button
                  variant="secondary"
                  className="w-full"
                  loading={busy}
                  onClick={() => void run('pause')}
                >
                  <PauseIcon data-icon="inline-start" />
                  暂停迁移
                </Button>
              ) : state!.state === 'paused' ? (
                <Button className="w-full" loading={busy} onClick={() => void run('resume')}>
                  <PlayIcon data-icon="inline-start" />
                  继续迁移
                </Button>
              ) : null}
            </div>
          </Card>
        </div>
      ) : null}

      {/* 逐类型筛选：数量上限 + 时间范围 */}
      {filterSheetType ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-[2px]"
          onClick={() => setFilterSheetType(null)}
        >
          <Card className={cn(CARD, 'w-full max-w-sm')} onClick={(e) => e.stopPropagation()}>
            <strong className="text-sm font-semibold">
              {TYPES.find((t) => t.key === filterSheetType)?.label} · 筛选
            </strong>
            <p className="text-muted-foreground mt-1 text-caption1 leading-snug">
              只影响本次迁移，用于先迁少量做测试。
            </p>

            <div className="mt-3">
              <p className="text-muted-foreground text-caption1">数量上限</p>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {COUNT_PRESETS.map((preset) => {
                  const selected = (filters[filterSheetType]?.maxCount ?? null) === preset;
                  return (
                    <button
                      key={String(preset)}
                      type="button"
                      onClick={() =>
                        updateFilter(filterSheetType, { maxCount: preset ?? undefined })
                      }
                      className={cn(
                        'rounded-control px-3 py-1.5 text-footnote',
                        selected ? 'bg-primary text-primary-foreground' : 'bg-muted/60',
                      )}
                    >
                      {preset == null ? '全部' : `${preset} 条`}
                    </button>
                  );
                })}
              </div>
            </div>

            {TIME_FILTERABLE.has(filterSheetType) ? (
              <div className="mt-3">
                <p className="text-muted-foreground text-caption1">时间范围（按发布时间）</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {TIME_RANGES.map((range) => {
                    const selected =
                      (filters[filterSheetType]?.timeRange ?? 'all') === range.key;
                    return (
                      <button
                        key={range.key}
                        type="button"
                        onClick={() =>
                          updateFilter(filterSheetType, {
                            timeRange: range.key === 'all' ? undefined : range.key,
                          })
                        }
                        className={cn(
                          'rounded-control px-3 py-1.5 text-footnote',
                          selected ? 'bg-primary text-primary-foreground' : 'bg-muted/60',
                        )}
                      >
                        {range.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <Button
              variant="secondary"
              className="mt-4 w-full"
              onClick={() => setFilterSheetType(null)}
            >
              完成
            </Button>
          </Card>
        </div>
      ) : null}
    </PageShell>
  );
}
